import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getEvent, deleteEvent } from '../lib/firestore/events';
import { getRSVP, createRSVP, updateRSVP } from '../lib/firestore/events';
import { getGroup } from '../lib/firestore/groups';
import { isEventCohost } from '../lib/firestore/eventCohosts';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import InviteButton from '../components/InviteButton';
import InviteModal from '../components/InviteModal';
import InviteFriendModal from '../components/InviteFriendModal';
import EventAttendees from '../components/EventAttendees';
import MessageBoard from '../components/MessageBoard';
import QRCodeModal from '../components/QRCodeModal';
import UserProfileModal from '../components/UserProfileModal';
import AuthModal from '../components/AuthModal';
import BroadcastModal from '../components/BroadcastModal';
import { formatEventDateTime } from '../lib/date-utils';
import { isEventChatAvailable } from '../lib/chat-utils';
import { canInviteToEvent } from '../lib/permissions';
import { isAdmin } from '../lib/firestore/admins';
import '../styles/EventView.css';

const EventView = () => {
  const { eventId } = useParams();
  const { user, loading: authLoading } = useAuthState();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInviteFriendModal, setShowInviteFriendModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [showRSVPPopup, setShowRSVPPopup] = useState(false);
  const [canInvite, setCanInvite] = useState(false);
  const [showChatPopup, setShowChatPopup] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingRSVPState, setPendingRSVPState] = useState(null); // 'going', 'interested', 'cancelled'

  const [event, setEvent] = useState(null);
  const [rsvp, setRsvp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingRSVP, setUpdatingRSVP] = useState(false);
  const [hostGroup, setHostGroup] = useState(null);
  const [hostUser, setHostUser] = useState(null);
  const [creatorUser, setCreatorUser] = useState(null); // User who created the event
  const [isCohost, setIsCohost] = useState(false);
  const [canEditEvent, setCanEditEvent] = useState(false); // Track if user can edit
  const [totalAttendees, setTotalAttendees] = useState(0);
  const [spotsLeft, setSpotsLeft] = useState(null);
  
  useEffect(() => {
    console.log('🔵 [EventView] useEffect triggered:', { eventId, hasUser: !!user, userObject: user });
    if (!eventId) {
      console.log('🔴 [EventView] Missing eventId');
      navigate('/home');
      return;
    }
    
    // Load event data even without auth (for invite links)
    loadEventData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, user]);


  const loadEventData = async () => {
    console.log('🔵 [EventView] ====== loadEventData STARTED ======');
    console.log('🔵 [EventView] EventId:', eventId);
    
    if (!eventId) {
      console.error('🔴 [EventView] Missing eventId');
      navigate('/home');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('🔵 [EventView] Calling getEvent...');
      console.log('🔵 [EventView] About to fetch event from Firestore...');
      const eventData = await getEvent(eventId);
      console.log('🟢 [EventView] ====== getEvent SUCCESS ======');
      console.log('🟢 [EventView] Event data loaded:', eventData);
      if (!eventData) {
        console.error('🔴 [EventView] Event not found:', eventId);
        setError('Event not found');
        setLoading(false);
        return;
      }
      setEvent(eventData);

      // Load host data (works without auth)
      if (eventData.hostType === 'group') {
        try {
          const group = await getGroup(eventData.hostId);
          setHostGroup(group);
        } catch (err) {
          console.warn('Could not load group:', err);
        }
      } else if (eventData.hostType === 'event') {
        // For sub events, load the parent event as the host
        try {
          const parentEvent = await getEvent(eventData.hostId);
          if (parentEvent) {
            // Store parent event in hostGroup state for consistent display
            setHostGroup({
              id: parentEvent.id,
              name: parentEvent.title,
              image: parentEvent.image || null,
            });
          }
        } catch (err) {
          console.warn('Could not load parent event:', err);
        }
      } else {
        try {
          const userDoc = await getDoc(doc(db, 'users', eventData.hostId));
          if (userDoc.exists()) {
            setHostUser(userDoc.data());
          }
        } catch (err) {
          console.warn('Could not load host user:', err);
        }
      }

      // Load creator data if different from host (for group events and event-hosted events/sub events)
      // For group events: show creator if createdBy exists and is different from hostId (group ID)
      // For sub events (hostType === 'event'): show creator if createdBy exists (hostId is parent event ID, so always different)
      // For profile events: show creator if createdBy exists and is different from hostId (user ID)
      console.log('🔵 [EventView] Checking creator data:', {
        createdBy: eventData.createdBy,
        hostType: eventData.hostType,
        hostId: eventData.hostId,
        isGroupEvent: eventData.hostType === 'group',
        isSubEvent: eventData.hostType === 'event',
        isProfileEvent: eventData.hostType === 'profile',
        createdByDifferentFromHost: eventData.createdBy !== eventData.hostId
      });
      
      if (eventData.createdBy) {
        // For group events: only show if creator is different from group ID
        // For sub events: always show creator (hostId is parent event ID, not a user ID)
        // For profile events: only show if creator is different from host user ID
        const shouldLoadCreator = 
          (eventData.hostType === 'event') || // Sub events: always show creator
          (eventData.hostType === 'group' && eventData.createdBy !== eventData.hostId) || // Group events: show if different
          (eventData.hostType === 'profile' && eventData.createdBy !== eventData.hostId); // Profile events: show if different
        
        console.log('🔵 [EventView] Should load creator?', shouldLoadCreator);
        
        if (shouldLoadCreator) {
          try {
            console.log('🔵 [EventView] Loading creator user:', eventData.createdBy);
            const creatorDoc = await getDoc(doc(db, 'users', eventData.createdBy));
            if (creatorDoc.exists()) {
              const creatorData = creatorDoc.data();
              console.log('🟢 [EventView] Creator loaded:', { firstName: creatorData.firstName, lastName: creatorData.lastName });
              setCreatorUser(creatorData);
            } else {
              console.warn('🔴 [EventView] Creator document does not exist:', eventData.createdBy);
            }
          } catch (err) {
            console.error('🔴 [EventView] Could not load creator user:', err);
          }
        }
      }

      // Only load user-specific data if logged in
      if (user) {
        try {
          const userIsCohost = await isEventCohost(eventId, user.uid);
          setIsCohost(userIsCohost);

          // Check if user can edit the event
          const userIsHost = (eventData.hostId === user.uid && eventData.hostType === 'profile') || 
                             (eventData.hostId === user.uid && !eventData.hostType); // Fallback for old events
          const userIsCreator = eventData.createdBy === user.uid;
          
          let userIsGroupOwnerOrCohost = false;
          if (eventData.hostType === 'group') {
            try {
              const { getGroupMember } = await import('../lib/firestore/groups');
              const member = await getGroupMember(eventData.hostId, user.uid);
              userIsGroupOwnerOrCohost = member?.role === 'owner' || member?.role === 'cohost';
            } catch (err) {
              console.warn('Error checking group membership:', err);
            }
          }
          const userIsAdmin = await isAdmin(user.uid);
          setCanEditEvent(userIsHost || userIsCreator || userIsCohost || userIsGroupOwnerOrCohost || userIsAdmin);

          const userRSVP = await getRSVP(eventId, user.uid);
          setRsvp(userRSVP);

          // Check if user can invite
          const userCanInvite = await canInviteToEvent(eventId, user.uid);
          setCanInvite(userCanInvite);

          // Don't auto-show RSVP popup - let users click the buttons on the page
          // This prevents the "giant ugly popup" and gives users control
          // if (user && !userRSVP && !isPast) {
          //   setShowRSVPPopup(true);
          // }
        } catch (err) {
          console.warn('Could not load user-specific data:', err);
          setCanInvite(false);
        }
      } else {
        // Check if there's a pending invite token (from invite link)
        const urlParams = new URLSearchParams(window.location.search);
        const inviteToken = urlParams.get('invite');
        if (inviteToken) {
          sessionStorage.setItem('pendingInviteToken', inviteToken);
        }
      }

      // Calculate total attendees and spots left (works without auth)
      await calculateSpotsAvailable(eventData);

      console.log('🟢 [EventView] ====== loadEventData COMPLETED SUCCESSFULLY ======');
      setLoading(false);
    } catch (err) {
      console.error('🔴 [EventView] ====== ERROR LOADING EVENT ======');
      console.error('🔴 [EventView] Error type:', err.constructor.name);
      console.error('🔴 [EventView] Error message:', err.message);
      console.error('🔴 [EventView] Error code:', err.code);
      console.error('🔴 [EventView] Full error:', err);
      setError(err.message || 'Failed to load event');
      setLoading(false);
      console.log('🔴 [EventView] Loading state set to false, error set');
    }
    console.log('🔵 [EventView] ====== loadEventData FINISHED ======');
  };

  const calculateSpotsAvailable = async (eventData) => {
    if (!eventData) return;
    
    try {
      const rsvpsRef = collection(db, 'events', eventData.id, 'rsvps');
      const rsvpsSnapshot = await getDocs(rsvpsRef);
      
      let total = 0;
      rsvpsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.state === 'going' || data.state === 'checked_in') {
          total += 1 + (data.plusOnes || 0);
        }
      });
      
      setTotalAttendees(total);
      
      if (eventData.capacity) {
        setSpotsLeft(Math.max(0, eventData.capacity - total));
      } else {
        setSpotsLeft(null); // Unlimited
      }
    } catch (err) {
      console.error('Error calculating spots:', err);
    }
  };

  const handleRSVP = async (state, plusOnes = 0) => {
    console.log('🔵 [EventView] ====== handleRSVP CALLED ======');
    console.log('🔵 [EventView] State:', state);
    console.log('🔵 [EventView] User object:', user);
    console.log('🔵 [EventView] User exists?', !!user);
    console.log('🔵 [EventView] Auth loading?', authLoading);
    console.log('🔵 [EventView] Event exists?', !!event);
    
    if (!event) {
      console.log('🔴 [EventView] No event, returning');
      return;
    }

    // Wait for auth to finish loading before checking user state
    if (authLoading) {
      console.log('🟡 [EventView] Auth still loading, waiting...');
      return;
    }

    // CRITICAL: Check if user is logged in FIRST - before any RSVP processing
    // If not logged in, show auth modal IMMEDIATELY and exit
    if (!user) {
      console.log('🟢 [EventView] ====== USER NOT LOGGED IN ======');
      console.log('🟢 [EventView] Opening auth modal for RSVP state:', state);
      setPendingRSVPState(state);
      const rsvpValue = state === 'interested' ? 'maybe' : state === 'cancelled' ? 'no' : 'going';
      sessionStorage.setItem('pendingRSVP', rsvpValue);
      console.log('🟢 [EventView] Stored pendingRSVP in sessionStorage:', rsvpValue);
      setShowAuthModal(true);
      console.log('🟢 [EventView] showAuthModal set to TRUE - modal should appear');
      console.log('🟢 [EventView] ====== EXITING - NO RSVP PROCESSING ======');
      return; // EXIT IMMEDIATELY - don't process RSVP
    }
    
    console.log('🔵 [EventView] ====== USER IS LOGGED IN ======');
    console.log('🔵 [EventView] User ID:', user.uid);
    console.log('🔵 [EventView] Processing RSVP directly for logged-in user');

    setUpdatingRSVP(true);
    try {
      // Check if there's a pending invite token to process
      const inviteToken = sessionStorage.getItem('pendingInviteToken');
      if (inviteToken) {
        // Process invite token (adds user to event, etc.)
        try {
          const { resolveInviteToken } = await import('../lib/invites');
          const invite = await resolveInviteToken(inviteToken);
          if (invite && invite.target.kind === 'event_join' && invite.target.id === eventId) {
            // Invite is valid and matches this event
            sessionStorage.removeItem('pendingInviteToken');
          }
        } catch (err) {
          console.warn('Could not process invite token:', err);
        }
      }

      if (rsvp) {
        await updateRSVP(eventId, user.uid, {
          state,
          plusOnes,
        });
      } else {
        await createRSVP({
          userId: user.uid,
          eventId,
          state,
          plusOnes,
        });
      }

      const updatedRSVP = await getRSVP(eventId, user.uid);
      setRsvp(updatedRSVP);
      
      // Recalculate spots
      await calculateSpotsAvailable(event);
      
      // Close popup (if it was open) and scroll to bottom
      setShowRSVPPopup(false);
      // Don't auto-scroll - let user see their RSVP was saved
    } catch (err) {
      setError(err.message || 'Failed to update RSVP');
    } finally {
      setUpdatingRSVP(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== 'DELETE' || !event) return;

    setDeleting(true);
    try {
      await deleteEvent(eventId);
      if (event.hostType === 'group') {
        navigate(`/groups/${event.hostId}`);
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete event');
      setDeleting(false);
    }
  };

  if (loading) {
    console.log('🔵 [EventView] Rendering loading state');
    return (
      <div className="event-view-container">
        <div className="loading">Loading event...</div>
        <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666' }}>
          {error ? `Error: ${error}` : 'Fetching event data...'}
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-view-container">
        <div className="error-message">{error || 'Event not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate('/home')}>
          Go Home
        </button>
      </div>
    );
  }

  const isPast = new Date(event.startsAt) < new Date();
  const isCheckedIn = rsvp?.state === 'checked_in';
  // Check if user can manage:
  // 1. User is the direct host (profile events)
  // 2. User created the event (createdBy field)
  // 3. User is an event cohost
  // 4. If event is hosted by a group: user is group owner/cohost (checked separately)
  const isHost = event.hostId === user?.uid && event.hostType === 'profile';
  const isCreator = event.createdBy === user?.uid;
  const canManage = isHost || isCreator || isCohost || canEditEvent;
  const canViewLocation = isHost || isCohost || rsvp?.state === 'going' || rsvp?.state === 'checked_in';
  
  // Get first letter for default avatar
  const firstLetter = event.title ? event.title.charAt(0).toUpperCase() : 'E';
  
  // Check if description is longer than a paragraph (roughly 150 chars)
  const descriptionLong = event.description && event.description.length > 150;
  const showDescriptionMore = descriptionLong && !descriptionExpanded;

  return (
    <div className="event-view-container">
      <main className="event-view-main">
        {/* 1. Event Name with Edit Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h1 className="event-title" style={{ margin: 0, flex: 1 }}>{event.title}</h1>
          {canEditEvent && (
            <button
              onClick={() => navigate(`/events/${eventId}/edit`)}
              className="btn-edit-event"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--background-alt)';
                e.currentTarget.style.borderColor = 'var(--primary-color)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'var(--border-light)';
              }}
            >
              <span>✏️</span>
              <span>Edit</span>
            </button>
          )}
        </div>

        {/* 2. Event Date and Time */}
        <div className="event-datetime">
          {formatEventDateTime(event.startsAt)}
        </div>

        {/* 2B. Event Picture - with Invite button top-right (like app); overlay after image so it paints on top */}
        <div className="event-image-section event-image-wrapper">
          {event.image ? (
            <img src={event.image} alt={event.title} className="event-image" />
          ) : (
            <div className="event-image-default">
              <span>{firstLetter}</span>
            </div>
          )}
          {(canInvite || canEditEvent) && user && (
            <div className="event-invite-overlay">
              <button
                type="button"
                className="event-invite-btn"
                onClick={() => setShowInviteModal(true)}
                title="Invite from contacts or friends (like app)"
              >
                Invite
              </button>
            </div>
          )}
        </div>

        {/* 3. Event Location */}
        <div className="event-location-section">
          {event.location?.text && event.location.text !== 'TBD' ? (
            canViewLocation ? (
              <div className="event-location">
                <span className="location-icon">📍</span>
                <span>{event.location.text}</span>
              </div>
            ) : (
              <div className="event-location-restricted">
                <span className="location-icon">📍</span>
                <span>RSVP to see full location</span>
              </div>
            )
          ) : (
            <div className="event-location">
              <span className="location-icon">📍</span>
              <span>Location TBD</span>
            </div>
          )}
        </div>

        {/* 4. Hosted By */}
        <div className="event-host-section">
          <div className="event-host-label">Hosted by</div>
          <div className="event-host-info">
            {hostGroup ? (
              <>
                {hostGroup.image && (
                  <img 
                    src={hostGroup.image} 
                    alt={hostGroup.name}
                    className="event-host-avatar"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                {!hostGroup.image && (
                  <div className="event-host-avatar event-host-avatar-default">
                    {hostGroup.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="event-host-name">{hostGroup.name}</span>
              </>
            ) : hostUser ? (
              <>
                {hostUser.profilePicture && (
                  <img 
                    src={hostUser.profilePicture} 
                    alt={hostUser.firstName || 'Host'}
                    className="event-host-avatar"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                {!hostUser.profilePicture && (
                  <div className="event-host-avatar event-host-avatar-default">
                    {(hostUser.firstName || 'H').charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="event-host-name">
                  {hostUser.firstName && hostUser.lastName
                    ? `${hostUser.firstName} ${hostUser.lastName}`
                    : hostUser.firstName || 'Host'}
                </span>
              </>
            ) : (
              <span className="event-host-name">Host</span>
            )}
          </div>
          {/* Show creator if different from host (for group events and event-hosted events/sub events) */}
          {hostGroup && creatorUser && (
            <div className="event-creator-info" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              <span>Created by </span>
              <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                {creatorUser.firstName && creatorUser.lastName
                  ? `${creatorUser.firstName} ${creatorUser.lastName}`.trim()
                  : creatorUser.firstName || creatorUser.email || 'Unknown User'}
              </span>
            </div>
          )}
        </div>

        {/* 5. Spots Available */}
        {event.capacity && (
          <div className="event-spots-section">
            <span className="spots-icon">👥</span>
            <span className="spots-text">
              {spotsLeft !== null ? `${spotsLeft}/${event.capacity} spots left` : `${event.capacity} spots`}
            </span>
          </div>
        )}

        {/* 6. Cost */}
        {event.rules?.costPerPerson !== undefined && event.rules.costPerPerson > 0 && (
          <div className="event-cost-section">
            <span className="cost-text">
              ${event.rules.costPerPerson.toFixed(2)} per person
            </span>
          </div>
        )}
        {event.rules?.costPerPerson === 0 && (
          <div className="event-cost-section">
            <span className="cost-text cost-free">Free</span>
          </div>
        )}

        {/* 7. Website */}
        {event.website && (
          <div className="event-website-section">
            <a 
              href={event.website} 
              target="_blank" 
              rel="noopener noreferrer"
              className="event-website-link"
            >
              <span className="website-icon">🔗</span>
              <span>Sign up here</span>
            </a>
          </div>
        )}

        {/* 8. Description with More button */}
        {event.description && (
          <div className="event-description-section">
            <div className={`event-description ${descriptionExpanded ? 'expanded' : ''}`}>
              {event.description}
            </div>
            {showDescriptionMore && (
              <button
                className="btn-description-more"
                onClick={() => setDescriptionExpanded(true)}
              >
                ...more
              </button>
            )}
            {descriptionExpanded && descriptionLong && (
              <button
                className="btn-description-more"
                onClick={() => setDescriptionExpanded(false)}
              >
                ...less
              </button>
            )}
          </div>
        )}

        {/* 9. Guest List with Invite/Chat Cards */}
        <div className="event-attendees-section">
          <EventAttendees 
            event={event} 
            rsvpState={rsvp?.state}
            showBlur={!rsvp || (rsvp.state !== 'going' && rsvp.state !== 'interested' && rsvp.state !== 'cancelled')}
            canInvite={canInvite || canEditEvent}
            canChat={isEventChatAvailable(event)}
            canBroadcast={canManage}
            onInviteClick={() => setShowInviteModal(true)}
            onInviteFriendClick={() => setShowInviteFriendModal(true)}
            onQRClick={() => setShowQRModal(true)}
            onChatClick={() => setShowChatPopup(true)}
            onBroadcastClick={() => setShowBroadcastModal(true)}
            onAttendeeClick={(userId) => {
              setSelectedUserId(userId);
              setShowProfileModal(true);
            }}
          />
        </div>

        {/* RSVP Buttons - Show for all users (authenticated or not) */}
        {!isPast && (
          <div className="event-rsvp-section">
            <div className="rsvp-buttons">
              <button
                className={`rsvp-button rsvp-going ${rsvp?.state === 'going' ? 'active' : ''}`}
                onClick={() => {
                  console.log('🔵 [EventView] Going button clicked');
                  handleRSVP('going', 0);
                }}
                disabled={updatingRSVP || authLoading}
              >
                <span className="rsvp-emoji">👍</span>
                <span className="rsvp-label">Going</span>
              </button>
              <button
                className={`rsvp-button rsvp-maybe ${rsvp?.state === 'interested' ? 'active' : ''}`}
                onClick={() => {
                  console.log('🔵 [EventView] Maybe button clicked');
                  handleRSVP('interested', 0);
                }}
                disabled={updatingRSVP || authLoading}
              >
                <span className="rsvp-emoji">🤔</span>
                <span className="rsvp-label">Maybe</span>
              </button>
              <button
                className={`rsvp-button rsvp-cant-go ${rsvp?.state === 'cancelled' ? 'active' : ''}`}
                onClick={() => {
                  console.log('🔵 [EventView] Can\'t Go button clicked');
                  handleRSVP('cancelled', 0);
                }}
                disabled={updatingRSVP || authLoading}
              >
                <span className="rsvp-emoji">😢</span>
                <span className="rsvp-label">Can't Go</span>
              </button>
            </div>
            {!user && (
              <p className="rsvp-hint">
                Sign in or create an account to RSVP and let the host know you're coming!
              </p>
            )}
          </div>
        )}

        {/* Unified Message Board */}
        {(event.messageBoards?.preEvent || event.messageBoards?.duringEvent || event.messageBoards?.decompression?.enabled) && (
          <div className="event-message-board">
            <MessageBoard eventId={eventId} event={event} />
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Delete Event</h2>
            <p className="modal-warning">
              This action cannot be undone. All RSVPs, check-ins, and event data will be permanently deleted.
            </p>
            <div className="modal-input-group">
              <label htmlFor="deleteConfirm">
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                id="deleteConfirm"
                type="text"
                className="input"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting || deleteConfirmText !== 'DELETE'}
              >
                {deleting ? 'Deleting...' : 'Delete Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && user && (
        <InviteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          eventId={eventId}
          eventTitle={event?.title || 'Event'}
          userId={user.uid}
        />
      )}

      {showInviteFriendModal && (
        <InviteFriendModal
          isOpen={showInviteFriendModal}
          onClose={() => setShowInviteFriendModal(false)}
          targetKind="event_join"
          targetId={eventId}
          targetName={event?.title}
        />
      )}

      {/* QR Code Modal */}
      {user && (
        <QRCodeModal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          eventId={eventId}
          issuedBy={user.uid}
        />
      )}

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
      />

      {/* Auth Modal (Signup/Login) */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setPendingRSVPState(null);
        }}
        eventId={eventId}
        onAuthSuccess={() => {
          // Close modal - EventView will reload when user state updates
          setShowAuthModal(false);
          setPendingRSVPState(null);
          // loadEventData will be called automatically when user state changes
        }}
      />

      {/* RSVP Popup Modal */}
      {!isPast && showRSVPPopup && (
        <div className="modal-overlay" onClick={() => setShowRSVPPopup(false)}>
          <div className="modal-content rsvp-popup" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">RSVP to Event</h2>
            <p className="modal-message">Will you be attending?</p>
            <div className="rsvp-popup-buttons">
              <button
                className={`rsvp-button rsvp-going`}
                onClick={() => handleRSVP('going', 0)}
                disabled={updatingRSVP}
              >
                <span className="rsvp-emoji">👍</span>
                <span className="rsvp-label">Going</span>
              </button>
              <button
                className={`rsvp-button rsvp-maybe`}
                onClick={() => handleRSVP('interested', 0)}
                disabled={updatingRSVP}
              >
                <span className="rsvp-emoji">🤔</span>
                <span className="rsvp-label">Maybe</span>
              </button>
              <button
                className={`rsvp-button rsvp-cant-go`}
                onClick={() => handleRSVP('cancelled', 0)}
                disabled={updatingRSVP}
              >
                <span className="rsvp-emoji">😢</span>
                <span className="rsvp-label">Can't Go</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Chat Popup */}
      {isEventChatAvailable(event) && (
        <EventChatPopup
          eventId={eventId}
          eventTitle={event.title}
          isOpen={showChatPopup}
          onClose={() => setShowChatPopup(false)}
        />
      )}

      {/* Broadcast Modal */}
      {user && event && (
        <BroadcastModal
          isOpen={showBroadcastModal}
          onClose={() => setShowBroadcastModal(false)}
          eventId={eventId}
          eventTitle={event.title}
          user={user}
        />
      )}
    </div>
  );
};

export default EventView;
